// ============================================================================
// chloe API クライアント
// ============================================================================
// API Gateway HTTP API (api.dev.chloe.goodanswers.inc) を叩く軽量 fetch ラッパ。
// - 認可ヘッダに Cognito の ID Token を自動で付与
// - 401 を 1 度だけ再試行（トークン期限切れ -> Amplify が refresh -> 再 fetch）
// - JSON エラー本文を Error.message に整形
// ----------------------------------------------------------------------------

import { getCurrentIdToken } from "./auth";

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || "").replace(/\/$/, "");

if (!API_BASE_URL) {
  console.warn("VITE_API_BASE_URL が未設定です。fetch は失敗します。");
}

export interface ApiOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  /** 認可不要のエンドポイントを叩く時のみ true */
  anonymous?: boolean;
}

export class ApiError extends Error {
  status: number;
  code?: string;
  details?: unknown;

  constructor(status: number, message: string, code?: string, details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

function buildUrl(path: string, query?: ApiOptions["query"]): string {
  const url = new URL(`${API_BASE_URL}${path.startsWith("/") ? path : `/${path}`}`);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null) continue;
      url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

async function buildHeaders(anonymous: boolean): Promise<Headers> {
  const headers = new Headers();
  headers.set("Content-Type", "application/json");
  headers.set("Accept", "application/json");
  if (!anonymous) {
    const token = await getCurrentIdToken();
    if (!token) {
      throw new ApiError(401, "認証セッションがありません。ログインし直してください。");
    }
    headers.set("Authorization", `Bearer ${token}`);
  }
  return headers;
}

/**
 * バックエンド (chloe-functions/_shared/response.ts) は以下の形式でエラーを返す:
 *   { error: { code: "BAD_REQUEST", message: "...", details?: ... } }
 * 旧形式（top-level に message / error 文字列）も後方互換で受け入れる。
 */
async function parseError(res: Response): Promise<ApiError> {
  let body: unknown = undefined;
  let message = `${res.status} ${res.statusText}`;
  let code: string | undefined;
  try {
    const text = await res.text();
    if (text) {
      try {
        body = JSON.parse(text);
        const errBody = body as {
          message?: unknown;
          error?: unknown;
          code?: string;
        };

        // 1) 新形式: { error: { code, message } }
        if (
          errBody.error &&
          typeof errBody.error === "object" &&
          errBody.error !== null
        ) {
          const errObj = errBody.error as { code?: string; message?: string };
          if (typeof errObj.message === "string" && errObj.message.length > 0) {
            message = errObj.message;
          }
          if (typeof errObj.code === "string") {
            code = errObj.code;
          }
        }
        // 2) 旧形式: top-level { message: "..." } 文字列
        else if (typeof errBody.message === "string" && errBody.message.length > 0) {
          message = errBody.message;
          if (typeof errBody.code === "string") {
            code = errBody.code;
          }
        }
        // 3) 旧形式: top-level { error: "..." } 文字列
        else if (typeof errBody.error === "string" && errBody.error.length > 0) {
          message = errBody.error;
        }
        // それ以外（schema 不明）はそのまま status + statusText で表示
      } catch (_err) {
        message = text;
      }
    }
  } catch (_err) {
    // ignore
  }
  return new ApiError(res.status, message, code, body);
}

async function request<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const { method = "GET", body, query, anonymous = false } = options;
  const url = buildUrl(path, query);

  const doFetch = async (): Promise<Response> =>
    fetch(url, {
      method,
      headers: await buildHeaders(anonymous),
      body: body !== undefined ? JSON.stringify(body) : undefined,
      // CORS: API GW 側は allow_credentials = false なので credentials は include しない
      credentials: "omit",
      mode: "cors",
    });

  let res = await doFetch();

  // 401 のみ 1 回だけリトライ（Amplify 側で token refresh が走る想定）
  if (res.status === 401 && !anonymous) {
    res = await doFetch();
  }

  if (!res.ok) {
    throw await parseError(res);
  }

  // 204 No Content は空オブジェクトとして返す
  if (res.status === 204) {
    return {} as T;
  }

  const text = await res.text();
  if (!text) return {} as T;
  return JSON.parse(text) as T;
}

export const api = {
  get<T>(path: string, options: Omit<ApiOptions, "method" | "body"> = {}): Promise<T> {
    return request<T>(path, { ...options, method: "GET" });
  },
  post<T>(path: string, body?: unknown, options: Omit<ApiOptions, "method" | "body"> = {}): Promise<T> {
    return request<T>(path, { ...options, method: "POST", body });
  },
  put<T>(path: string, body?: unknown, options: Omit<ApiOptions, "method" | "body"> = {}): Promise<T> {
    return request<T>(path, { ...options, method: "PUT", body });
  },
  delete<T>(path: string, options: Omit<ApiOptions, "method" | "body"> = {}): Promise<T> {
    return request<T>(path, { ...options, method: "DELETE" });
  },
};
